import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Offcanvas } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { faPen } from '@fortawesome/pro-solid-svg-icons/faPen';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { useIsNarrowScreen } from '../../../work/useIsNarrowScreen';
import { platformName } from '../../../platformMeta';
import { isSocialPlatform } from '../../../types';
import { planDayDate, planDayTitle, planStepTitle, planStepWhen } from '../rolloutDayCopy';
import { ChannelMark } from './ChannelMark';
import { useRollout } from './RolloutContext';
import type { PlanStepView } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
import { sheetMessages } from './sheetMessages';
import { SkipStepDialog } from './SkipStepDialog';
import { StepActions } from './StepActions';
import { StepLines } from './StepLines';
import { StepSheetAsset } from './StepSheetAsset';
import { StepSheetChannels } from './StepSheetChannels';
import { StepSheetPost } from './StepSheetPost';
import { StepThumb } from './StepThumb';
import { useOpenStepInComposer } from './useOpenStepInComposer';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the step sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
import sheetStyles from './StepSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

const FORMAT_LABEL = {
  story: rolloutMessages.formatStory,
  post: rolloutMessages.formatPost,
  reel: rolloutMessages.formatReel,
} as const;

interface StepSheetProps {
  step: PlanStepView | null;
  /** The channel tab to open first (`?channel=`). */
  platform: string | null;
  onClose: () => void;
}

/**
 * One step opened (design rollout-plan c1, c3; d1 below 700 px as a bottom sheet): the post as each
 * channel shows it, its time in the artist's time zone, the asset with a swap, the channels of the
 * step, "Skip this step" and "Open in Composer". A step whose asset is missing shows what it needs
 * and the Make button (c3). A step of an AI reels plan whose reel failed says so and has "Make it
 * again" (f3). Esc and the X close it.
 */
const StepSheetComponent: React.FC<StepSheetProps> = ({ step, platform, onClose }) => {
  const intl = useIntl();
  const isNarrow = useIsNarrowScreen();
  const { canAct, item, plan } = useRollout();
  const isReels = plan.kind === 'reels';
  const openInComposer = useOpenStepInComposer();
  const [tab, setTab] = useState<string | null>(platform);
  const [isSkipOpen, setIsSkipOpen] = useState(false);
  useEffect(() => setTab(platform), [platform, step?.key]);

  const channel = step === null ? undefined : (step.channels.find((one) => one.platform === tab) ?? step.channels[0]);
  const unplanned = step !== null && step.channels.every((one) => one.state === 'notPlanned');
  const isMissing = step !== null && step.assetState === 'missing' && unplanned;
  const missingMessage = step?.day.asset?.themeId === 'aiReel' ? sheetMessages.missingReel : sheetMessages.missingAsset;

  return (
    <Offcanvas
      isOpen={step !== null}
      toggle={onClose}
      direction={isNarrow ? 'bottom' : 'end'}
      className={isNarrow ? sheetStyles.sheetBottom : sheetStyles.sheet}
      aria-labelledby="rollout-step-title"
    >
      {step !== null && (
        <>
          <div className={sheetStyles.head}>
            <div className={sheetStyles.headRow}>
              <div className="d-flex flex-column gap-1">
                <span className={styles.eyebrow}>
                  {intl.formatMessage(sheetMessages.eyebrow, {
                    offset: planStepWhen(intl, plan, step.day.offsetDays, step.day.key),
                    day: planDayDate(intl, step.date),
                  })}
                </span>
                <h3 id="rollout-step-title">{planStepTitle(intl, plan, step.day)}</h3>
                <span className={sheetStyles.sub}>
                  {intl.formatMessage(sheetMessages.sub, {
                    format: intl.formatMessage(FORMAT_LABEL[step.day.format]),
                    count: step.channels.length,
                  })}
                </span>
              </div>
              <button
                type="button"
                className={sheetStyles.close}
                aria-label={intl.formatMessage(sheetMessages.close)}
                onClick={onClose}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {!isMissing && step.channels.length > 0 && (
              <div className={sheetStyles.tabs} role="tablist">
                {step.channels.map((one) => (
                  <button
                    key={one.platform}
                    type="button"
                    role="tab"
                    aria-selected={one === channel}
                    className={clsx(sheetStyles.tab, one === channel && sheetStyles.tabOn)}
                    onClick={() => setTab(one.platform)}
                  >
                    <ChannelMark platform={one.platform} small />
                    {isSocialPlatform(one.platform) ? platformName(one.platform) : one.platform}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={sheetStyles.body}>
            {isMissing ? (
              <div className={sheetStyles.missing}>
                <StepThumb day={step.day} />
                <div className="d-flex flex-column gap-2">
                  <b>
                    {isReels
                      ? planStepTitle(intl, plan, step.day)
                      : intl.formatMessage(sheetMessages.missingTitle, {
                          asset: planDayTitle(intl, { key: '', asset: step.day.asset }),
                        })}
                  </b>
                  {!isReels && (
                    <span className={styles.line}>{intl.formatMessage(missingMessage, { title: item.title })}</span>
                  )}
                  {isReels && <StepLines step={step} />}
                  <StepActions step={step} />
                </div>
              </div>
            ) : (
              <>
                {channel !== undefined && <StepSheetPost step={step} channel={channel} />}
                <StepSheetAsset step={step} />
              </>
            )}
            <StepSheetChannels step={step} />
          </div>
          <div className={sheetStyles.foot}>
            <button type="button" className={sheetStyles.skip} disabled={!canAct} onClick={() => setIsSkipOpen(true)}>
              <FontAwesomeIcon icon={faXmark} />
              {intl.formatMessage(sheetMessages.skip)}
            </button>
            <span className={styles.grow} />
            {!isMissing && (
              <SMLButton
                icon={<FontAwesomeIcon icon={faPen} />}
                onClick={() => openInComposer(step, channel?.platform ?? null)}
              >
                {intl.formatMessage(sheetMessages.openComposer)}
              </SMLButton>
            )}
          </div>
          <SkipStepDialog
            step={step}
            isOpen={isSkipOpen}
            onClose={() => setIsSkipOpen(false)}
            onSkipped={() => {
              setIsSkipOpen(false);
              onClose();
            }}
          />
        </>
      )}
    </Offcanvas>
  );
};

export const StepSheet = TypedMemo(StepSheetComponent);
